import { Sentry } from '@thistle/mainframe-sentry';
import { Cycle, Facility, APIRoutePrefixWithStatus, APIRouteWithStatus, APIPackLine, PackLine, CreatePackLineRequest } from '../types/index.js';
import { ENV } from '../config/environment.js';
import { authService } from '@thistle/mainframe-auth';
import { actions } from '../store/appStore.js';

const BASE_URL = ENV.apiBaseUrl;


// Track last invocation time for each endpoint to prevent over-refreshing in the data refresh service
export const lastInvocationAt: Record<string, number> = {
  routePrefixes: 0,
  packLines: 0,
  packLineDetails: 0,
  routes: 0,
  deliveries: 0,
};

export class ApiError extends Error {
  public status: number;
  public code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    // Get valid (non-expired) token
    const idToken = await authService.getValidIdToken();
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    } else {
      actions.addAlert("Signed out. Please sign in.", "error", "global", true);
      throw new ApiError(401, 'No valid token');
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    // Handle authentication failures
    if (response.status === 401) {
      throw new ApiError(401, 'Server rejected token');
    }

    const result = await response.json();

    if (!response.ok) {
      // Handle HTTPException with detailed error response (nested under 'detail')
      if (result && result.detail && result.detail.error_code && result.detail.message) {
        throw new ApiError(response.status, result.detail.message, result.detail.error_code);
      }
      // Handle direct error response format
      if (result && result.error_code && result.message) {
        throw new ApiError(response.status, result.message, result.error_code);
      }
      throw new ApiError(response.status, `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle API response format
    if (result.success !== undefined) {
      if (!result.success) {
        throw new ApiError(400, result.message || 'API request failed', result.error_code);
      }
      return result.data;
    }

    return result;
  } catch (error) {
    if (error instanceof ApiError) {
      Sentry.captureException(error, {
        extra: { endpoint, status: error.status, code: error.code },
      });
      throw error;
    }
    const apiError = new ApiError(0, `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    Sentry.captureException(apiError, { extra: { endpoint } });
    throw apiError;
  }
}


export const api = {
  // **Fetching Data**

  // Fetch facilities
  async getFacilities(): Promise<Facility[]> {
    return fetchApi<Facility[]>('/facilities');
  },

  // Fetch current cycle based on PT timezone rules
  async getCurrentCycle(): Promise<Cycle> {
    return fetchApi<Cycle>('/cycles/current');
  },

  // Fetch route prefixes by cycle ID and facility ID
  async getRoutePrefixesByCycleId(cycleId: number, facilityId: number): Promise<APIRoutePrefixWithStatus[]> {
    const result = await fetchApi<APIRoutePrefixWithStatus[]>(`/cycles/${cycleId}/route-prefixes?facility_id=${facilityId}`);
    lastInvocationAt.routePrefixes = Date.now();
    return result;
  },

  // Fetch routes for a specific route prefix
  async getRoutesByRoutePrefix(cycleId: number, routePrefixId: number, facilityId: number): Promise<APIRouteWithStatus[]> {
    const result = await fetchApi<APIRouteWithStatus[]>(`/cycles/${cycleId}/route-prefixes/${routePrefixId}/routes?facility_id=${facilityId}`);
    lastInvocationAt.routes = Date.now();
    return result;
  },

  // Fetch deliveries for a specific route
  async getDeliveriesByRouteId(routeId: number): Promise<any[]> {
    const result = await fetchApi<any[]>(`/routes/${routeId}/deliveries`);
    lastInvocationAt.deliveries = Date.now();
    return result;
  },

  // Fetch pack lines by cycle ID and facility ID
  async getPackLinesByCycleId(cycleId: number, facilityId: number): Promise<APIPackLine[]> {
    const result = await fetchApi<APIPackLine[]>(`/cycles/${cycleId}/pack-lines?facility_id=${facilityId}`);
    lastInvocationAt.packLines = Date.now();
    return result;
  },

  // Get pack line details with routes
  async getPackLineDetails(packLineId: number): Promise<APIPackLine> {
    const result = await fetchApi<APIPackLine>(`/pack-lines/${packLineId}`);
    lastInvocationAt.packLineDetails = Date.now();
    return result;
  },

  // **Modifying Data**

  // Create new pack line
  async createPackLine(packLine: CreatePackLineRequest): Promise<any> {
    return fetchApi<any>('/pack-lines', {
      method: 'POST',
      body: JSON.stringify(packLine)
    });
  },

  // Manage pack session (start, break, end)
  async managePackSession(packLineId: number, action: 'start' | 'break' | 'end', employeeTempCount?: number, employeeExemptCount?: number): Promise<any> {
    return fetchApi<any>(`/pack-lines/${packLineId}/session`, {
      method: 'PUT',
      body: JSON.stringify({
        action,
        employee_temp_count: employeeTempCount,
        employee_exempt_count: employeeExemptCount
      })
    });
  },

  // Connect device to pack line
  async linkDeviceToPackline(packLineId: number): Promise<any> {
    return fetchApi<any>(`/pack-lines/${packLineId}/link`, {
      method: 'PUT'
    });
  },

  // Scan delivery QR code
  async scanDeliveryQR(packLineId: number, qrCode: string): Promise<any> {
    return fetchApi<any>('/deliveries/scan', {
      method: 'POST',
      body: JSON.stringify({
        pack_line_id: packLineId,
        qr_code: qrCode
      })
    });
  },

  // Update delivery status
  async updateDeliveryStatus(deliveryId: number, status: 'pending' | 'omitted' | 'manual_entry', submittedBy?: string, packLineId?: number): Promise<any> {
    return fetchApi<any>(`/deliveries/${deliveryId}/status`, {
      method: 'PUT',
      body: JSON.stringify({
        status,
        submitted_by: submittedBy,
        pack_line_id: packLineId
      })
    });
  }
};
